import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import Select from "react-select";

import { get as getAccounts } from "../../../actions/accounts";
import { deepEqual } from "../../../utils/object";

const mapPropsToState = (props) => ({
  required1099: Boolean(props.required1099),
  expenseAccountUuid: props.expenseAccountUuid || null,
  meta: props.meta || null,
});

const accountLabel = (account) => {
  const number = String(account?.accountNumber || "").trim();
  const name = String(account?.name || "").trim();
  if (number && name) return `${number} - ${name}`;
  return name || number || account?.uuid || "";
};

const Vendor = forwardRef(function Vendor(props, ref) {
  const {
    required1099,
    expenseAccountUuid,
    meta,
    getAccounts,
  } = props;

  const mappedProps = useMemo(
    () => mapPropsToState({ required1099, expenseAccountUuid, meta }),
    [required1099, expenseAccountUuid, meta]
  );

  const [vendor, setVendor] = useState(mappedProps);
  const [accounts, setAccounts] = useState([]);
  const initialValueRef = useRef(mappedProps);
  const prevMappedRef = useRef(mappedProps);

  useEffect(() => {
    let mounted = true;
    getAccounts(null).then((result) => {
      if (mounted) setAccounts(result || []);
    });
    return () => { mounted = false; };
  }, [getAccounts]);

  useEffect(() => {
    if (deepEqual(prevMappedRef.current, mappedProps)) return;
    setVendor(mappedProps);
    prevMappedRef.current = mappedProps;
    initialValueRef.current = mappedProps;
  }, [mappedProps]);

  const updateField = useCallback((name, value) => {
    setVendor((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const expenseAccountOptions = useMemo(() => (
    (accounts || [])
      .filter((account) => account?.active !== false)
      .filter((account) => ["EXPENSE", "COGS", "ASSET"].includes(account?.type))
      .map((account) => ({
        value: account.uuid,
        label: accountLabel(account),
      }))
  ), [accounts]);

  const selectedExpenseAccount = expenseAccountOptions
    .find((option) => option.value === vendor.expenseAccountUuid) || null;

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => ({ ...vendor }),
      setValue: (value = {}) => {
        const next = mapPropsToState(value);
        setVendor(next);
      },
      reset: (value = null) => {
        const next = value ? mapPropsToState(value) : initialValueRef.current;
        setVendor(next);
      },
      validate: () => ({ success: true, errors: {} }),
      isDirty: () => !deepEqual(initialValueRef.current, vendor),
    }),
    [vendor]
  );

  return (
    <Col xs="12">
      <Row className="g-3">
        <Col lg="12">
          <h2 className="small-title">Vendor Information</h2>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="6">
          <div className="mb-3 top-label">
            <Select
              classNamePrefix="react-select"
              isClearable
              value={selectedExpenseAccount}
              options={expenseAccountOptions}
              placeholder="(Select expense account)"
              onChange={(option) => updateField("expenseAccountUuid", option?.value || null)}
            />
            <Form.Label>DEFAULT EXPENSE ACCOUNT</Form.Label>
          </div>
        </Col>
        <Col md="6">
          <Form.Check
            type="switch"
            id="vendor-required1099"
            className="mt-3"
            label="Requires 1099"
            checked={Boolean(vendor.required1099)}
            onChange={(event) => updateField("required1099", event.target.checked)}
          />
        </Col>
      </Row>
    </Col>
  );
});

export default connect(null, { getAccounts }, null, { forwardRef: true })(Vendor);
